"use client";
import ModalUpcoming from "@/app/[locale]/(user-page)/report/components/Modal/ModalUpcoming";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import { useAppDispatch } from "@/lib/hooks";
import { paramsReport } from "@/models/Params";
import { TYPE_FAVORITE } from "@/models/report";
import ReportServices from "@/services/main/reportServices";
import { roundNumberToK } from "@/utils";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Box, Link as LinkMui, Tooltip, Typography } from "@mui/material";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import Link from "next/link";
import { redirect } from "next/navigation";
import { useState } from "react";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import "./styles.css";

interface UpcomingReportsProps {
  isLoggedIn: boolean;
  categoryId?: string | number;
  queryKey?: string;
  onlyFavorite?: boolean;
  userId?: number;
  isMobile?: boolean;
  typeFavorite?: number[];
  commingReports?: any;
  type: string;
  locale: string;
}

const montserrat = Montserrat({
  subsets: ["latin"],
});

const UpcomingReports = (props: UpcomingReportsProps) => {
  const {
    isLoggedIn,
    categoryId,
    queryKey,
    onlyFavorite,
    userId,
    isMobile,
    typeFavorite,
    commingReports,
    type,
    locale,
  } = props;
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);
  const [dataModalUpcoming, setDataModalUpcoming] = useState(null);
  const t = useTranslations("messages");
  const dispatch = useAppDispatch();
  const [centeredSlides, setCenteredSlides] = useState(false);
  const handleCloseModal = () => setOpenModal(false);
  const handleClickOpenModal = (item: any) => {
    setDataModalUpcoming(item);
    setOpenModal(true);
  };

  const { data: upcomingData, isFetching } = useInfiniteQuery({
    queryKey: [queryKey, categoryId, type],
    queryFn: async ({ pageParam }: { pageParam?: number | string }) => {
      const params: paramsReport = {
        take: 6,
        page: pageParam as any,
        onlyFavorite: type === "favorite" ? true : undefined,
        userId: type === "favorite" ? userId : undefined,
        categoryIds: categoryId,
        typeFavorite:
          type === "favorite" ? [TYPE_FAVORITE.FAVORITE] : undefined,
        lang: locale,
      };
      const result = await ReportServices.getAllReportCommingSoon(params);
      return result;
    },
    placeholderData: keepPreviousData,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.hasMore) {
        return lastPage.meta.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      return firstPage.meta.page > 1 ? firstPage.meta.page - 1 : undefined;
    },
    select: (data: any) => ({
      data: (data.pages as any).flatMap((item: any): Report[] => item.data),
      hasMore: (data.pages as any).flatMap((item: any) => item.meta.hasMore),
    }),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const AddFavorite = useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      return result;
    },
  });

  const handleFavorite = async (
    id: number,
    currentFavorites: boolean,
    currentCount: number
  ) => {
    if (!isLoggedIn) {
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname +
            window.location.search +
            window.location.hash
          : "";
      redirect(
        `${APP_ROUTES(locale).LOGIN.to}?redirect=${encodeURIComponent(currentPath)}`
      );
      return;
    }
    const isCurrentlyFavorited = currentFavorites;
    const newFavorites = isCurrentlyFavorited ? false : true;
    const newCount = isCurrentlyFavorited
      ? Number(currentCount) - 1
      : Number(currentCount) + 1;
    queryClient.setQueryData([queryKey, categoryId, type], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          data: page.data.map((item: any) =>
            item.id === id
              ? {
                  ...item,
                  isFavorite: newFavorites,
                  favoritesCount: newCount,
                }
              : item
          ),
        })),
      };
    });
    try {
      await AddFavorite.mutateAsync(id);
    } catch {
      queryClient.setQueryData([queryKey, categoryId, type], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.map((item: any) =>
              item.id === id
                ? {
                    ...item,
                    isFavorite: currentFavorites,
                    favoritesCount: currentCount,
                  }
                : item
            ),
          })),
        };
      });
    }
  };

  //MOBILE// Khi lướt tới slide thứ 2 trở lên thì centeredSlides: true
  const handleSlideChange = (swiper: any) => {
    if (swiper.activeIndex >= 1 && !centeredSlides) {
      setTimeout(() => setCenteredSlides(true), 200);
    }
  };
  return isFetching && !(upcomingData as any)?.data?.length ? null : (
    <>
      {upcomingData?.data?.length > 0 ? (
        <div className="overflow-hidden">
          <Box className="sm:flex justify-between items-center py-4">
            <Box className="flex items-center gap-2">
              <Image
                src={image.iconCampaign}
                alt="iconCampaign"
                className="!h-8 w-8"
                style={{
                  objectFit: "contain",
                  width: "auto",
                  height: "auto",
                }}
              />
              <p
                className={`text-eerie_black font-semibold ${isMobile ? "text-[24px]/[100%]" : "text-2xl/[100%]"} sm:text-[32px]/[100%] m-0`}
                translation-key="home_upcoming_reports"
                translation-sub-key="home_upcoming_reports_cast_your_vote"
              >
                {t("home_upcoming_reports")}{" "}
                {!isLoggedIn ? "" : t("home_upcoming_reports_cast_your_vote")}
              </p>
            </Box>

            <Box className="flex justify-end mt-5 sm:mt-0">
              <Link
                // onClick={handldeReUpcoming}
                className="text-ciBlue_900 !text-base/6 !font-semibold hover:underline hover:cursor-pointer no-underline report-see-all-upcoming-reports-link"
                href={APP_ROUTES(locale).UPCOMING.to}
                translation-key="home_see_all"
              >
                {t("home_see_all")}
              </Link>
            </Box>
          </Box>
          <Box>
            <Swiper
              loop={isMobile ? true : false}
              slidesPerView={"auto"}
              spaceBetween={16}
              allowTouchMove={true}
              simulateTouch={true}
              touchRatio={1}
              touchAngle={45}
              className={` !px-0 sm:!px-0 !object-hidden sm:h-40 h-auto `}
              centeredSlides={isMobile ? centeredSlides : false}
              onSlideChange={isMobile ? handleSlideChange : undefined}
              breakpoints={{
                0: {
                  slidesPerView: "auto",
                  spaceBetween: 16,
                  centeredSlides: isMobile ? centeredSlides : false,
                },
                640: {
                  slidesPerView: "auto",
                  spaceBetween: 16,
                },
              }}
            >
              {upcomingData?.data?.map((item: any) => (
                <SwiperSlide
                  className={`!w-[312px] rounded-lg !h-[90px] border border-solid border-gray-200 group hover:cursor-pointer !mr-0 ${
                    !isMobile
                      ? "hover:shadow-[0px_1px_6px_rgba(0,0,0,0.15)] !p-6"
                      : !centeredSlides
                        ? "!ml-[3.5px] !pr-[0px] !p-[1.1rem]"
                        : "!ml-[0px] !pr-[0px] !p-[1.1rem]"
                  }`}
                  key={`${categoryId}-${item?.id}`}
                  onClick={() => handleClickOpenModal(item)}
                >
                  <motion.div className="grid gap-6">
                    <LinkMui
                      className="!font-semibold !text-base/6 !text-eerie_black h-12 group-hover:!text-ciBlue_900 m-0 overflow-hidden text-ellipsis "
                      underline="none"
                    >
                      {item?.title}
                    </LinkMui>
                    <Box className="flex items-center justify-between">
                      <Box className="overflow-hidden">
                        <p
                          className={`text-[#494949] font-medium text-sm/[17.07px] m-0 truncate ${isLoggedIn ? "max-w-[210px]" : "max-w-[260px]"}`}
                        >
                          {item?.categories.map(
                            (itemCategory: any, index: any) =>
                              itemCategory?.name +
                              `${item?.categories.length - 1 > index ? ", " : ""}`
                          )}
                        </p>
                      </Box>
                      {isLoggedIn && (
                        <Box className="flex items-center">
                          <Typography className=" !text-gray-600 !font-medium !text-sm !mr-4 sm:!mr-[5px]">
                            {roundNumberToK(item?.favoritesCount || 0)}
                          </Typography>
                          <Tooltip
                            title={
                              <span className={`${montserrat.className}`}>
                                {
                                  item?.isFavorite
                                    ? t("common_favorited") // "Bỏ yêu thích"
                                    : t("common_favorite") // "Thêm vào yêu thích"
                                }
                              </span>
                            }
                            placement="bottom"
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: {
                                      offset: [0, -10], // Điều chỉnh vị trí của tooltip
                                    },
                                  },
                                ],
                              },
                            }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="border-0 py-1 px-1 bg-white hover:bg-white cursor-pointer rounded flex justify-center "
                              onClick={(event) => {
                                event.stopPropagation();
                                handleFavorite(
                                  item?.id,
                                  item?.isFavorite,
                                  item?.favoritesCount
                                );
                              }}
                              aria-label={
                                item?.isFavorite
                                  ? t("common_favorited")
                                  : t("common_favorite")
                              }
                            >
                              {item?.isFavorite ? (
                                <FavoriteIcon className="h-6 w-6 text-warning" />
                              ) : (
                                <FavoriteBorderIcon className="h-6 w-6 text-warning" />
                              )}
                            </motion.button>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </div>
      ) : null}
      <ModalUpcoming
        open={openModal}
        handleClose={handleCloseModal}
        isLoggedIn={isLoggedIn}
        dataModalUpcoming={dataModalUpcoming}
        queryKeyUpcoming={[queryKey, categoryId, type]}
        locale={locale}
      />
    </>
  );
};

export default UpcomingReports;
